import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

const AdminUniversityPartnerReport = () => {
  const navigate = useNavigate();

  // Data state
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 100;

  // Filter state
  const [cycleYear, setCycleYear] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [currentCycleYear, setCurrentCycleYear] = useState(null);
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [universities, setUniversities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Summary stats
  const [summary, setSummary] = useState({
    total_students: 0,
    total_universities: 0,
    block_grant_scholars: 0,
    continuing_students: 0
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('university');
  const [sortOrder, setSortOrder] = useState('asc');

  // Export state
  const [exporting, setExporting] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    if (!cycleYear) return;

    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedUniversity) {
        params.append('university', selectedUniversity);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/university-partner-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setReport(response.data.report);
        setTotalPages(response.data.pagination.total_pages);
        setTotalRecords(response.data.pagination.total_records);
        setCurrentPage(response.data.pagination.page);
        setUniversities(response.data.filters.universities);
        setSummary(response.data.summary);

        // Set available cycles if not already set
        if (response.data.filters.cycle_years && availableCycles.length === 0) {
          setAvailableCycles(response.data.filters.cycle_years);
          const current = response.data.filters.cycle_years.find(c => c.is_current);
          if (current) {
            setCurrentCycleYear(current.year);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [cycleYear, currentPage, sortBy, sortOrder, searchTerm, selectedUniversity, availableCycles.length]);

  // Initial load - get cycle years first
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = getValidToken();
        const response = await axios.get(
          `${API_URL}/api/admin/university-partner-report?page=1&per_page=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.filters.cycle_years) {
          setAvailableCycles(response.data.filters.cycle_years);
          const current = response.data.filters.cycle_years.find(c => c.is_current);
          if (current) {
            setCurrentCycleYear(current.year);
            setCycleYear(current.year);
          } else if (response.data.filters.cycle_years.length > 0) {
            setCycleYear(response.data.filters.cycle_years[0].year);
          }
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        const currentYear = new Date().getFullYear();
        setCycleYear(currentYear);
        setAvailableCycles([{ year: currentYear, is_current: true, student_count: 0 }]);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch report when dependencies change
  useEffect(() => {
    if (cycleYear) {
      fetchReport();
    }
  }, [fetchReport, cycleYear]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return sortOrder === 'asc'
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedUniversity) {
        params.append('university', selectedUniversity);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/university-partner-report/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Get display name from universities array for a cleaner filename
      const selectedUnivDisplay = universities.find(u => u.value === selectedUniversity)?.name || selectedUniversity;
      const univSuffix = selectedUniversity ? `_${selectedUnivDisplay.replace(/\s+/g, '_')}` : '';
      link.setAttribute('download', `university_partner_report_cycle${cycleYear}${univSuffix}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle filter changes
  const handleCycleChange = (e) => {
    setCycleYear(parseInt(e.target.value));
    setCurrentPage(1);
    setSelectedUniversity('');
  };

  const handleUniversityChange = (e) => {
    setSelectedUniversity(e.target.value);
    setCurrentPage(1);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (!status) return <span className="text-gray-400">-</span>;

    const statusLower = status.toLowerCase();
    if (statusLower === 'active') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>;
    } else if (statusLower === 'inactive') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Inactive</span>;
    } else if (statusLower === 'withdrawn') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Withdrawn</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{status}</span>;
  };

  // Get block grant badge
  const getBlockGrantBadge = (value) => {
    if (!value) return <span className="text-gray-400">-</span>;

    const lowerValue = value.toLowerCase();
    if (lowerValue === 'yes' || lowerValue === 'y') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Yes</span>;
    }
    if (lowerValue === 'no' || lowerValue === 'n') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">No</span>;
    }
    // Display raw value for any other data
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">{value}</span>;
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 my-4">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded border ${
              currentPage === page
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Last
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">University Partner Report</h1>
              <p className="text-sm text-gray-600 mt-1">Student data by university for the selected cycle</p>
              <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 max-w-2xl">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span><strong>Verified Data Only:</strong> Data are not self-reported by students and reflect the last official update obtained by NACME from university partners.</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border rounded hover:bg-gray-50"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
            <div className="text-2xl font-bold text-blue-700">{summary.total_students}</div>
            <div className="text-sm text-gray-600">Total Students</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-400">
            <div className="text-2xl font-bold text-indigo-700">{summary.total_universities}</div>
            <div className="text-sm text-gray-600">Universities</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
            <div className="text-2xl font-bold text-purple-700">{summary.block_grant_scholars}</div>
            <div className="text-sm text-gray-600">Block Grant Scholars</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <div className="text-2xl font-bold text-green-700">{summary.continuing_students}</div>
            <div className="text-sm text-gray-600">Continuing</div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Cycle Year Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Year</label>
              <select
                value={cycleYear || ''}
                onChange={handleCycleChange}
                className="px-3 py-2 border rounded-md min-w-[180px] bg-white"
              >
                {availableCycles.map(cycle => (
                  <option key={cycle.year} value={cycle.year}>
                    {cycle.year} {cycle.is_current ? '(Current)' : ''} - {cycle.student_count} students
                  </option>
                ))}
              </select>
            </div>

            {/* University Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
              <select
                value={selectedUniversity}
                onChange={handleUniversityChange}
                className="px-3 py-2 border rounded-md min-w-[280px] bg-white"
              >
                <option value="">All Universities</option>
                {universities.map(univ => (
                  <option key={univ.value} value={univ.value}>
                    {univ.name} ({univ.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name or major..."
                className="px-3 py-2 border rounded-md min-w-[250px]"
              />
            </div>

            {/* Export Button */}
            <div className="ml-auto">
              <button
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <span className="animate-spin">...</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Pagination Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 text-lg">i</span>
              <span className="text-blue-800 font-medium">
                Displaying {perPage} records per page | Page {currentPage} of {totalPages} | {totalRecords.toLocaleString()} total students
              </span>
            </div>
            {cycleYear === currentCycleYear && (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Current Cycle
              </span>
            )}
          </div>
        </div>

        {/* Top Pagination */}
        {totalPages > 1 && <Pagination />}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-auto max-h-[600px]">
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th
                    onClick={() => handleSort('university')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 sticky left-0 z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                  >
                    University {renderSortIcon('university')}
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-cyan-700 uppercase tracking-wider bg-cyan-50 border-x-2 border-cyan-300"
                  >
                    ID
                  </th>
                  <th
                    onClick={() => handleSort('first_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    First Name {renderSortIcon('first_name')}
                  </th>
                  <th
                    onClick={() => handleSort('last_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Last Name {renderSortIcon('last_name')}
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Full Name {renderSortIcon('name')}
                  </th>
                  <th
                    onClick={() => handleSort('major')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Major {renderSortIcon('major')}
                  </th>
                  <th
                    onClick={() => handleSort('class_year')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Class Year {renderSortIcon('class_year')}
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    DOB
                  </th>
                  <th
                    onClick={() => handleSort('citizenship_status')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Citizenship {renderSortIcon('citizenship_status')}
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Self-reported GPA
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Confirmed GPA*
                  </th>
                  <th
                    onClick={() => handleSort('block_grant_scholar')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Block Grant {renderSortIcon('block_grant_scholar')}
                  </th>
                  <th
                    onClick={() => handleSort('applicant_status')}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Status {renderSortIcon('applicant_status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="animate-spin text-2xl">...</span>
                        Loading report data...
                      </div>
                    </td>
                  </tr>
                ) : report.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                      No students found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  report.map((item, index) => (
                    <tr key={`${item.student_id}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium sticky left-0 z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        {item.university || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-cyan-700 bg-cyan-50 border-x-2 border-cyan-100">
                        {item.student_id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.first_name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.last_name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.major || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.class_year || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.dob || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.citizenship_status || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.self_reported_gpa || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                        {item.confirmed_gpa || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        {getBlockGrantBadge(item.block_grant_scholar)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        {getStatusBadge(item.applicant_status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Footnote */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
            <span className="font-medium">*Confirmed GPA</span> is from the Last Official Update obtained by NACME. This is not self-reported data from the student's NACME Searchlight application.
          </div>
        </div>

        {/* Bottom Pagination */}
        {totalPages > 1 && <Pagination />}
      </main>
    </div>
  );
};

export default AdminUniversityPartnerReport;
